/**
 * The per-customer-site rows under an expanded Supplier Performance row —
 * Customer review 11 Sep (item 3/4, the old portal's m04b model).
 */
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { SupplierProfile, SupplierRatingThreshold } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PerformanceTableRow, RowCallbacks } from './PerformanceTableRow';
import { useSiteBreakdown } from './useSiteBreakdown';

interface SiteBreakdownRowsProps extends RowCallbacks {
  supplier: SupplierProfile;
  thresholds: SupplierRatingThreshold[];
  currency: string;
  columnCount: number;
  buildSiteRowParams: (siteId: number, supplierId: number) => Record<string, string | number | boolean>;
}

export function SiteBreakdownRows({
  supplier,
  thresholds,
  currency,
  columnCount,
  buildSiteRowParams,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: SiteBreakdownRowsProps) {
  const sites = supplier.customer_sites ?? [];
  const { data, loading, error, refetch } = useSiteBreakdown(
    supplier.company, sites, buildSiteRowParams, true,
  );

  const message = (content: ReactNode, key: string) => (
    <tr key={key} className="border-b bg-slate-50 dark:bg-slate-900">
      <td colSpan={columnCount} className="p-3 pl-12 text-xs text-muted-foreground">
        <div className="sticky left-12 inline-flex items-center gap-2">{content}</div>
      </td>
    </tr>
  );

  if (loading) {
    return message(
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading {supplier.company_name} at each of {sites.length} customer sites…
      </>,
      'loading',
    );
  }
  if (error) {
    return message(
      <>
        <span className="text-red-600">Could not load the per-site figures: {error}</span>
        <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => refetch()}>
          Retry
        </Button>
      </>,
      'error',
    );
  }

  return (
    <>
      {(data ?? []).map(({ site, profile }) =>
        profile ? (
          <PerformanceTableRow
            key={`site-${supplier.company}-${site.id}`}
            supplier={profile}
            thresholds={thresholds}
            currency={currency}
            viewerIsSupplier={false}
            showSupplierSite={false}
            variant="site"
            site={site}
            onOpenGRNModal={onOpenGRNModal}
            onOpenNCRModal={onOpenNCRModal}
            onOpenPOLineModal={onOpenPOLineModal}
          />
        ) : (
          message(`${site.name}: no figures for ${supplier.company_name} in this period`, `empty-${site.id}`)
        ),
      )}
    </>
  );
}
